import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Radio } from "lucide-react";
import { toast } from "sonner";
import LiveTvStage from "../components/LiveTvStage";
import OnDemandInstallCta from "../components/OnDemandInstallCta";
import RadioNowPlayingCard from "../components/RadioNowPlayingCard";
import { STATIONS } from "../config/stations";
import { applyManifest, MAIN_MANIFEST, ON_DEMAND_MANIFEST } from "../lib/manifest-switch";

const RADIO_STATIONS = STATIONS.filter((s) => s.kind === "audio");

const buildSrcDoc = () => `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>GFN Soul On Demand</title>
    <style>
      html, body { margin: 0; padding: 0; background: #0a0a0a; color: #fff; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
      body { padding: 12px 8px 32px; }
      * { box-sizing: border-box; max-width: 100%; }
    </style>
  </head>
  <body>
    <div id="tvab-mount"></div>
    <script
      src="https://tvappbuilder.com/embed/embed.js"
      data-church="958"
      data-layout="carousel"
      data-theme="dark"
      data-search="true"
      data-sort="true"
      data-default-sort="newest"
      data-load-more="true"
      data-live-feed="true"
    ></script>
  </body>
</html>`;

const GfnSoulOnDemand = () => {
  const srcDoc = useMemo(buildSrcDoc, []);
  const { search } = useLocation();

  useEffect(() => {
    applyManifest(ON_DEMAND_MANIFEST);
    if (new URLSearchParams(search).get("install") === "1") {
      toast("Tap “Download Web App” to install GFN Soul On Demand", { duration: 6000 });
    }
    return () => applyManifest(MAIN_MANIFEST);
  }, [search]);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10" data-testid="ondemand-page">
      {/* Gold header bar */}
      <div
        data-testid="ondemand-header"
        className="rounded-t-lg bg-gradient-to-r from-gold-dark via-gold to-gold-dark px-6 md:px-10 py-6 flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <img
            src="/icon-ondemand-192.png"
            alt="GFN Soul On Demand"
            className="w-12 h-12 rounded-xl shadow-lg"
            data-testid="ondemand-header-logo"
          />
          <div>
            <div className="font-condensed uppercase tracking-[0.35em] text-[10px] text-black/70">GFN Presents</div>
            <div className="font-display text-3xl md:text-4xl leading-none text-black uppercase">GFN Soul On Demand</div>
          </div>
        </div>
        <div className="hidden sm:block text-right">
          <div className="font-condensed uppercase tracking-widest text-[10px] text-black/70">Live + Catalog</div>
          <div className="font-condensed uppercase text-sm text-black">Watch anytime</div>
        </div>
      </div>

      {/* Live channel on top + install CTA */}
      <div className="bg-jet-surface border-x border-gold/20 px-4 md:px-8 py-6 grid lg:grid-cols-[1.6fr_1fr] gap-6 items-stretch">
        <LiveTvStage testId="ondemand-live-stage" />
        <OnDemandInstallCta />
      </div>

      {/* Radio streams — right under the live channel so they're always in reach */}
      <section
        className="bg-jet-surface border-x border-gold/20 px-4 md:px-8 pb-6"
        data-testid="ondemand-radio-section"
      >
        <div className="flex items-center gap-2 mb-3">
          <Radio className="w-3.5 h-3.5 text-gold" />
          <span className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">Listen Live · GFN Radio</span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {RADIO_STATIONS.map((s) => (
            <RadioNowPlayingCard key={s.id} station={s} />
          ))}
        </div>
      </section>

      <div className="bg-jet-surface border-x border-gold/20 px-6 md:px-10 pb-4">
        <div className="font-condensed uppercase tracking-[0.35em] text-[10px] text-gold">On Demand Catalog</div>
        <p className="font-body text-neutral-300 text-sm md:text-base max-w-3xl mt-1">
          Browse the carousel, search episodes, sort by newest, and press play. The on-demand player
          runs in-page — your radio stream keeps playing at the bottom.
        </p>
      </div>

      {/* Embed — isolated in an iframe */}
      <div className="bg-jet-surface border-x border-b border-gold/20 rounded-b-lg p-2 md:p-4">
        <div className="rounded-md bg-[#0a0a0a] overflow-hidden" data-testid="ondemand-embed-frame">
          <iframe
            title="GFN Soul On Demand Catalog"
            srcDoc={srcDoc}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
            allow="autoplay; fullscreen; picture-in-picture"
            className="w-full block"
            style={{ height: "1100px", border: "0" }}
          />
        </div>
      </div>

      <div className="mt-6 text-center font-condensed uppercase tracking-[0.35em] text-[10px] text-neutral-500">
        Powered by TV App Builder · Wrapped in the GFN broadcast look
      </div>
    </div>
  );
};

export default GfnSoulOnDemand;
